import { Box, Button, IconButton, Paper, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AddIcon from '@mui/icons-material/Add'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { createElement } from 'react'
import { ElementFactory, QuestionPanelDynamicModel, Serializer } from 'survey-core'
import { ReactQuestionFactory, SurveyQuestionElementBase } from 'survey-react-ui'
import { styles } from './SurveyPanelDynamic.styles'

const CUSTOM_TYPE = 'surveypaneldynamic'

export class SurveyPanelDynamicModel extends QuestionPanelDynamicModel {
  constructor (name: string) {
    super(name)
    this.titleLocation = 'hidden'
  }

  getType (): string {
    return CUSTOM_TYPE
  }

  get panelItemTitle (): string {
    return this.getPropertyValue('panelItemTitle') ?? ''
  }

  set panelItemTitle (value: string) {
    this.setPropertyValue('panelItemTitle', value)
  }

  get showGroupTitle (): boolean {
    return this.getPropertyValue('showGroupTitle') ?? false
  }

  set showGroupTitle (value: boolean) {
    this.setPropertyValue('showGroupTitle', value)
  }

  get iconOnlyRemove (): boolean {
    return this.getPropertyValue('iconOnlyRemove') ?? false
  }

  set iconOnlyRemove (value: boolean) {
    this.setPropertyValue('iconOnlyRemove', value)
  }
}

export function registerPanelDynamic (): void {
  ElementFactory.Instance.registerElement(
    CUSTOM_TYPE,
    (name) => {
      return new SurveyPanelDynamicModel(name)
    }
  )

  Serializer.addClass(
    CUSTOM_TYPE,
    [
      { name: 'panelItemTitle', type: 'text', category: 'general', default: '' },
      { name: 'showGroupTitle', type: 'boolean', category: 'general', default: false },
      { name: 'iconOnlyRemove', type: 'boolean', category: 'general', default: false }
    ],
    function () {
      return new SurveyPanelDynamicModel('')
    },
    'paneldynamic'
  )

  ReactQuestionFactory.Instance.registerQuestion(CUSTOM_TYPE, (props) => {
    return createElement(SurveyQuestionPanelDynamic, props)
  })
}

export class SurveyQuestionPanelDynamic extends SurveyQuestionElementBase {
  get question (): SurveyPanelDynamicModel {
    return this.questionBase as SurveyPanelDynamicModel
  }

  renderElement (): JSX.Element {
    const panels = this.question.panels
    const addText = this.question.panelAddText ?? 'Add New'
    const itemTitleTemplate = this.question.panelItemTitle
    const showGroupTitle = this.question.showGroupTitle
    const iconOnlyRemove = this.question.iconOnlyRemove
    const panelHeading = (index: number): string =>
      itemTitleTemplate !== '' ? itemTitleTemplate.replace('{n}', String(index + 1)) : `${this.question.title} (${index + 2})`

    return (
      <Box sx={styles.container}>
        {showGroupTitle && (this.question.title !== '' || this.question.description !== '') && (
          <Box sx={{ mb: '8px' }}>
            {this.question.title !== '' && <Typography variant="h5">{this.question.title}</Typography>}
            {this.question.description !== '' && <Typography variant="body2" color="text.secondary">{this.question.description}</Typography>}
          </Box>
        )}
        {panels.map((panel, index) => (
          <Paper key={panel.id} elevation={0} className='surveyPanelDynamicPaper' sx={styles.paper}>
            <Box sx={styles.header}>
              <Box sx={styles.title}>
                {panelHeading(index)}
              </Box>
              {this.question.canRemovePanel && (iconOnlyRemove
                ? (
                  <IconButton onClick={() => { this.question.removePanelUI(index) }} aria-label="delete" size="small">
                    <DeleteOutlineIcon />
                  </IconButton>
                  )
                : (
                  <IconButton onClick={() => { this.question.removePanelUI(index) }} aria-label="delete" color="primary" size="small">
                    Delete <DeleteIcon />
                  </IconButton>
                  ))}
            </Box>

            {panel.questions.map((q) => (
              <Box key={q.name} sx={styles.questionWrapper}>
                {this.creator.createQuestionElement(q)}
              </Box>
            ))}
          </Paper>
        ))}

        {this.question.canAddPanel && (
          <Button
            endIcon={iconOnlyRemove ? <AddCircleOutlineIcon /> : <AddIcon />}
            onClick={() => this.question.addPanelUI()}
            sx={styles.addButton}
            data-testid={`${this.question.name}-add-button`}
          >
            {addText}
          </Button>
        )}
      </Box>
    )
  }
}
